import React, { useState, useEffect, useContext, useRef } from "react";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { makeStyles } from 'tss-react/mui';
import {
	Grid,
	FormControl,
	InputAdornment,
	CircularProgress,
	DialogTitle,
	DialogContent,
	DialogActions,
	Dialog,
	TextField,
	Button,
	IconButton,
	InputLabel,
	Select,
	MenuItem,
	Box,
	Tooltip
} from "@mui/material";
import HelpIcon from '@mui/icons-material/Help';
import { MuiColorInput } from 'mui-color-input';
import { green } from "@mui/material/colors";
import { i18n } from "../../translate/i18n";
import ConfirmationMultiCompaniesModal from "../ConfirmationMultiCompaniesModal";
import api from "../../services/api";
import Colorize from '@mui/icons-material/Colorize';
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import ToastSuccess from "../../toast/success/toastSuccess";
import ToastError from "../../toast/error/toastError";

const useStyles = makeStyles()((theme) => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	btnWrapper: {
		position: "relative",
	},
	buttonProgress: {
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
	formControl: {
		margin: '10px',
		minWidth: 120,
	},
	colorAdorment: {
		width: 20,
		height: 20,
	},
	loadingContainer: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		height: '100%',
	},
}));

const CreateOrEditKanbanTagModal = ({ open, onClose, tagId, kanbanId }) => {
	const { classes } = useStyles();
	const greetingRef = useRef();
	const { user: loggedInUser } = useContext(AuthContext);
	const initialState = { companyId: loggedInUser.companyId, title: "", color: "", isGlobal: false };
	const [tag, setTag] = useState(initialState);
	const [errors, setErrors] = useState({});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [loading, setLoading] = useState(false);

	const TagSchema = Yup.object().shape({
		title: Yup.string().min(2, "Muito Curto(a)!").max(400, "Muito Longo(a)!").required("Obrigatório"),
		color: Yup.string().required(),
	});

	useEffect(() => {
		const fetchUser = async () => {
			if (!tagId) return;
			try {
				setLoading(true);
				const { data } = await api.get(`/kanban/tag/${tagId}`);
				setTag(prevState => ({ ...prevState, ...data }));
			} catch (err) {
				ToastError(err);
			} finally {
				setLoading(false); 
			}
		};
		fetchUser();
	}, [tagId, open]);

	const handleChange = (e) => {
		const { name, value } = e.target;
		console.info({ name, value })
		setTag(prevState => ({ ...prevState, [name]: value }));
	};

	const handleColorChange = (color) => {
		setTag(prevState => ({ ...prevState, color }));
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setIsSubmitting(true);
		try {
			await TagSchema.validate(tag, { abortEarly: false });
			const tagData = { ...tag };
			if (tagId) {
				await api.put(`/kanban/tag/${tagId}`, tagData);
			} else {
				await api.post(`/kanban/tag/${kanbanId}`, tagData);
			}
			ToastSuccess('Marcador cadastrado com sucesso');
			handleClose();
		} catch (err) {
			if (err instanceof Yup.ValidationError) {
				const validationErrors = {};
				err.inner.forEach((error) => {
					validationErrors[error.path] = error.message;
				});
				setErrors(validationErrors);
			} else {
				ToastError(err);
			}
		}
		setIsSubmitting(false);
	};

	const handleClose = () => {
		onClose();
		setTag(initialState);
		setErrors({});
	};

	return (
		<div className={classes.root}>
			<Dialog
				open={open}
				onClose={handleClose}
				maxWidth="xs"
				fullWidth
				scroll="body"
				BackdropProps={{
					style: {
						backdropFilter: 'blur(3px)',
						backgroundColor: 'rgba(0, 0, 30, 0.4)',
					},
				}}
			>
				<DialogTitle id="form-dialog-title">
					{tagId ? `${i18n.t("tagList.editTag")}` : `${i18n.t("tagList.addTag")}`}
				</DialogTitle>
				{loading ? ( // Se estiver carregando, mostra o CircularProgress
					<Box className={classes.loadingContainer}>
						<CircularProgress />
					</Box>
				) : (
					<form onSubmit={handleSubmit}>
						<DialogContent dividers className={classes.teste}>
							<Grid container spacing={2}>
								<Grid item xs={12} sm={12}>
									<TextField
										style={{ display: 'none' }}
										type="hidden"
										name="tagId"
										value={tagId}
									/>
									<TextField
										size="small"
										label={i18n.t("tagList.name")}
										type="text"
										name="title"
										value={tag.title}
										onChange={handleChange}
										inputRef={greetingRef}
										error={Boolean(errors.title)}
										helperText={errors.title}
										required
										autoFocus
										variant="outlined"
										margin="dense"
										fullWidth
									/>
								</Grid>
								<Grid item xs={12} sm={6}>
									<MuiColorInput
										format="hex"
										label={i18n.t("tagList.color")}
										name="color"
										size="small"
										value={tag.color}
										onChange={handleColorChange}
										inputRef={greetingRef}
										error={Boolean(errors.color)}
										helperText={errors.color}
										variant="outlined"
										margin="dense"
										fullWidth
									/>
								</Grid>
								<Grid item xs={12} sm={6}>
									<FormControl fullWidth size="small" sx={{ marginTop: '8px' }}>
										<InputLabel id="tag-select-label">Exibir para todos</InputLabel>
										<Select
											name="isGlobal"
											labelId="tag-select-label"
											id="tag-select"
											value={tag.isGlobal}
											label="Exibir para todos"
											onChange={handleChange}
											renderValue={(selected) => (
												<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
													<Tooltip
														title={
															<div style={{
																maxWidth: '280px',
																padding: '12px',
																borderRadius: '8px',
																color: '#fff',
																fontSize: '0.875rem',
																lineHeight: '1.5',
																textAlign: 'left'
															}}>
																<strong>Exibir para todos</strong>
																<p style={{ margin: '8px 0 0' }}>
																	Ao ativar esta opção, o marcador criado será exibido em todos os quadros, sem vínculo exclusivo com o quadro atual (Global).
																</p>
															</div>
														}
														arrow
														placement="top-start"
														enterTouchDelay={0}
														leaveTouchDelay={500}
													>
														<HelpIcon fontSize="small" style={{ cursor: 'pointer' }} />
													</Tooltip>
													<span style={{ fontWeight: 500 }}>
														{selected ? 'Habilitado' : 'Desabilitado'}
													</span>
												</div>
											)}
										>
											<MenuItem value={true}>Habilitado</MenuItem>
											<MenuItem value={false}>Desabilitado</MenuItem>
										</Select>
									</FormControl>
								</Grid>

							</Grid>
						</DialogContent>
						<DialogActions>
							<Button
								onClick={handleClose}
								color="secondary"
								disabled={isSubmitting}
								variant="outlined"
							>
								{i18n.t("tagList.cancel")}
							</Button>
							<Button
								type="submit"
								color="primary"
								disabled={isSubmitting}
								variant="contained"
								className={classes.btnWrapper}
							>
								{tagId ? `${i18n.t("tagList.edit")}` : `${i18n.t("tagList.add")}`}
								{isSubmitting && (
									<CircularProgress
										size={24}
										className={classes.buttonProgress}
									/>
								)}
							</Button>
						</DialogActions>
					</form>
				)}
			</Dialog>
		</div >
	);
};

export default CreateOrEditKanbanTagModal;
